/**
 * Builds UserStatisticsRequest from tab-specific filters so the stats cards
 * reflect the same scope as the current tab (students / teachers / classes).
 *
 * Endpoint: POST /admin-management/{version}/metrics/user-statistics
 * Optional body: groupIds, teacherIds, gradeIds, studentIds, statuses, filterByCurrentLevel
 */

import { UserStatisticsRequest } from '@/school-management/types/response';
import {
  FilterField,
  FilterValue,
  isEmptyValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import commonUtils from '@/school-management/utils/common';
import { UserStatusEnum } from '@/school-management/types/response/studentDetails';
import {
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
} from '@/school-management/types/response/autocomplete';

/** Date filters affect table rows only; top metrics use current state (see ELL-7401). */
export const STUDENT_TAB_DATE_FILTER_KEYS = ['firstLogin', 'lastLogin'] as const;

/**
 * True when any non-date student-tab filter is active
 */
export function hasStudentTabStatsScopeFilters(
  filterFields: FilterField[],
  filters: Record<string, FilterValue>
): boolean {
  return filterFields.some(f => {
    if (!f || f.dateMode) return false;
    return filters[f.key] ? !isEmptyValue(f, filters[f.key]) : false;
  });
}

/** Student tab filter keys and how they map to the API */
const STUDENT_NAME_KEY = 'name';
const STUDENT_TEACHER_KEY = 'teacher';
const STUDENT_GROUP_KEY = 'groupName';
const STUDENT_GRADE_KEY = 'gradeName';
const STUDENT_STATUS_KEY = 'status';
const STUDENT_DISPLAY_DATA_FOR_KEY = 'displayDataFor';

/** Teachers tab uses key 'teachers' for multi teacher select */
const TEACHER_IDS_KEY = 'teachers';

/** Classes tab filter keys */
const CLASS_GROUP_KEY = 'groupName';
const CLASS_TEACHER_KEY = 'teacher';
const CLASS_GRADE_KEY = 'grade';

function toTeacherIds(value: FilterValue | undefined): string[] {
  if (!value || !Array.isArray(value)) return [];
  const items = value as TeacherAutocompleteItem[];
  return items.map(t => t.id).filter(Boolean);
}

function toGroupIds(value: FilterValue | undefined): string[] {
  if (!value || !Array.isArray(value)) return [];
  const items = value as GroupAutocompleteItem[];
  return items.map(g => g.groupId).filter(Boolean);
}

function toStudentIds(value: FilterValue | undefined): string[] {
  if (!value || !Array.isArray(value)) return [];
  const items = value as StudentAutocompleteItem[];
  return items.map(s => s.id).filter(Boolean);
}

function toGradeIds(value: FilterValue | undefined, t: (key: string) => string): number[] {
  if (!value || !Array.isArray(value)) return [];
  const labels = (value as string[]).filter(v => v && v !== 'All');
  if (labels.length === 0) return [];
  const mapper = commonUtils.getGradesLabelToIdMapper(t);
  return labels.map(g => mapper[g]).filter((id): id is number => typeof id === 'number');
}

/**
 * Map translated status labels to backend enum strings.
 * Backend expects statuses like "ACTIVE", "INACTIVE", etc.
 */
function toStatuses(value: FilterValue | undefined, t: (key: string) => string): string[] {
  if (!value || !Array.isArray(value)) return [];
  const labels = (value as string[]).filter(v => v && v !== 'All');
  if (labels.length === 0) return [];
  const labelToEnum: Record<string, UserStatusEnum[]> = {
    [t('active')]: [
      UserStatusEnum.ACTIVE,
      UserStatusEnum.LEVEL_DOWN,
      UserStatusEnum.LEVEL_UP,
      UserStatusEnum.LEVEL_COMPLETED_BLOCKED,
    ],
    [t('inactive')]: [UserStatusEnum.INACTIVE],
    [t('levelTestIncomplete')]: [UserStatusEnum.LEVEL_TEST_INCOMPLETE],
    [t('resetPassword')]: [UserStatusEnum.RESET_PASSWORD],
  };
  return labels.reduce((acc, s) => {
    const status = labelToEnum[s] || [];
    acc = [...acc, ...status];
    return acc;
  }, [] as UserStatusEnum[]);
}

export interface BuildUserStatisticsPayloadParams {
  activeTab?: string;
  studentFilters?: Record<string, FilterValue>;
  teacherFilters?: Record<string, FilterValue>;
  classFilters?: Record<string, FilterValue>;
  classesTabLockedTeacherIds?: string[];
  filterByCurrentLevel?: boolean;
  t: (key: string) => string;
}

/**
 * Builds the optional body for POST metrics/user-statistics from the active tab's filters.
 * - students tab: studentFilters → groupIds, teacherIds, gradeIds, studentIds, statuses
 * - teachers tab: teacherFilters → teacherIds
 * - classes tab: classFilters → groupIds, teacherIds, gradeIds
 * - general / no tab: no filters
 */
export function buildUserStatisticsPayload({
  activeTab,
  studentFilters,
  teacherFilters,
  classFilters,
  classesTabLockedTeacherIds,
  filterByCurrentLevel = false,
  t,
}: BuildUserStatisticsPayloadParams): UserStatisticsRequest {
  const payload: UserStatisticsRequest = {};

  if (filterByCurrentLevel) {
    payload.filterByCurrentLevel = true;
  }

  switch (activeTab) {
    case 'students': {
      if (!studentFilters) break;
      const groupIds = toGroupIds(studentFilters[STUDENT_GROUP_KEY]);
      const teacherIds = toTeacherIds(studentFilters[STUDENT_TEACHER_KEY]);
      const gradeIds = toGradeIds(studentFilters[STUDENT_GRADE_KEY], t);
      const studentIds = toStudentIds(studentFilters[STUDENT_NAME_KEY]);
      const statuses = toStatuses(studentFilters[STUDENT_STATUS_KEY], t);
      if (groupIds.length > 0) payload.groupIds = groupIds;
      if (teacherIds.length > 0) payload.teacherIds = teacherIds;
      if (gradeIds.length > 0) payload.gradeIds = gradeIds;
      if (studentIds.length > 0) payload.studentIds = studentIds;
      if (statuses.length > 0) payload.statuses = statuses;
      payload.filterByCurrentLevel =
        studentFilters[STUDENT_DISPLAY_DATA_FOR_KEY] === 'currentLevel';
      break;
    }
    case 'teachers': {
      if (!teacherFilters) break;
      const teacherIds = toTeacherIds(teacherFilters[TEACHER_IDS_KEY]);
      if (teacherIds.length > 0) payload.teacherIds = teacherIds;
      break;
    }
    case 'classes': {
      if (!classFilters) break;
      const groupIds = toGroupIds(classFilters[CLASS_GROUP_KEY]);
      const teacherIdsFromFilter = toTeacherIds(classFilters[CLASS_TEACHER_KEY]);
      const teacherIds =
        teacherIdsFromFilter.length > 0
          ? teacherIdsFromFilter
          : (classesTabLockedTeacherIds ?? []).filter(Boolean);
      const gradeIds = toGradeIds(classFilters[CLASS_GRADE_KEY], t);
      if (groupIds.length > 0) payload.groupIds = groupIds;
      if (teacherIds.length > 0) payload.teacherIds = teacherIds;
      if (gradeIds.length > 0) payload.gradeIds = gradeIds;
      break;
    }
    default:
      // general / school details: no filters
      break;
  }

  return payload;
}
